import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMdas } from '../services/api';

function ServiceMenu({ services = [], onFilterChange }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [mdaList, setMdaList] = useState([]);
    const [mdaLoading, setMdaLoading] = useState(true);
    const [mdaError, setMdaError] = useState(null);
    const [selectedMDAs, setSelectedMDAs] = useState(new Set());
    const [showFilters, setShowFilters] = useState(true);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const navigate = useNavigate();

    // ✅ Detect screen size changes dynamically
    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ✅ Collapse filter by default on small screens
    useEffect(() => {
        setShowFilters(!isSmallScreen);
    }, [isSmallScreen]);

    useEffect(() => {
        setMdaLoading(true);
        setMdaError(null);
        getMdas()
            .then(response => {
                setMdaList(response.data || []);
            })
            .catch(err => {
                console.error("Error fetching MDAs:", err);
                setMdaError("Could not load MDAs");
                setMdaList([]);
            })
            .finally(() => {
                setMdaLoading(false);
            });
    }, []);

    const filteredSuggestions = useMemo(() => {
        if (!searchTerm) return [];
        const lowerCaseSearch = searchTerm.toLowerCase();
        return services
            .filter(service => service.name.toLowerCase().includes(lowerCaseSearch))
            .slice(0, 10);
    }, [searchTerm, services]);

    useEffect(() => {
        setSuggestions(filteredSuggestions);
    }, [filteredSuggestions]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleSuggestionClick = (serviceId) => {
        navigate(`/service/${serviceId}`);
        setSearchTerm('');
        setSuggestions([]);
    };

    const handleMDAChange = (event) => {
        const { name, checked } = event.target;
        const updatedSelection = new Set(selectedMDAs);
        if (checked) {
            updatedSelection.add(name);
        } else {
            updatedSelection.delete(name);
        }
        setSelectedMDAs(updatedSelection);
        if (onFilterChange) {
            onFilterChange(Array.from(updatedSelection));
        }
    };

    return (
        <div className="service-menu">
            <style>{`
                .filter-toggle-btn {
                background-color: #198b44;
                color: white;
                border: none;
                border-radius: 0.375rem;
                padding: 0.5rem 1rem;
                cursor: pointer;
                margin-top: 0.75rem;
                width: 100%;
                font-weight: 600;
                transition: background 0.2s ease;
                }

                .filter-toggle-btn:hover {
                background-color: #157a37;
                }

                /* Smooth folding animation */
                .filter-section {
                transition: all 0.3s ease;
                }
            `}</style>
            {/* --- Search Section --- */}
            <div className="menu-section search-section">
                <h3>Search for Service</h3>
                <input
                    type="text"
                    placeholder="Enter service name..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="search-input"
                />

                {suggestions.length > 0 && (
                    <ul className="suggestions-list">
                        {suggestions.map(service => (
                            <li
                                key={service.id}
                                onClick={() => handleSuggestionClick(service.id)}
                                className="suggestion-item"
                            >
                                {service.name}
                            </li>
                        ))}
                    </ul>
                )}

                {/* ✅ Dropdown Toggle (only on small screens) */}
                {isSmallScreen && (
                    <button
                        className="filter-toggle-btn"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        {showFilters ? 'Hide Filters ▲' : 'Show Filters ▼'}
                    </button>
                )}
            </div>

            {/* --- Filter Section --- */}
            {(!isSmallScreen || showFilters) && (
                <div className="menu-section filter-section">
                    <h3>Filter Services by Provider</h3>
                    {mdaLoading ? (
                        <p>Loading MDAs...</p>
                    ) : mdaError ? (
                        <p style={{ color: 'red' }}>{mdaError}</p>
                    ) : mdaList.length === 0 ? (
                        <p>No MDAs available.</p>
                    ) : (
                        <div className="mda-list-scroll-container">
                            <ul className="mda-list">
                                {mdaList
                                    .filter(mda => [
                                        "Preventative Maintenance (Horticultural Services Dept)",
                                        "Valuation & Property Management"
                                    ].includes(mda.fullName.trim()))
                                    .map(mda => (
                                        <li key={mda.id} className="mda-item">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    className="checkbox-mda"
                                                    name={mda.fullName}
                                                    checked={selectedMDAs.has(mda.fullName)}
                                                    onChange={handleMDAChange}
                                                />
                                                {mda.fullName}
                                            </label>
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ServiceMenu;
